import { mapProductToSlimProduct } from '$store/components/product/ProductCard.tsx'
import ProductCard from '$store/components/product/ProductCard.tsx'
import { SealConfig } from '$store/loaders/seals.tsx'
import { TimerConfig } from '$store/loaders/timers.tsx'
import { useSeals } from '$store/sdk/useSeals.ts'
import { useTimers } from '$store/sdk/useTimers.ts'
import { PageInfo, Product } from 'apps/commerce/types.ts'
// import type { Product as ProductSTD } from 'deco-sites/std/commerce/types.ts'
// import { ProductInfo } from 'deco-sites/std/components/VTEXPortalDataLayerCompatibility.tsx'
import InfiniteScroll from 'site/islands/InfiniteScroll.tsx'
import { useId } from 'preact/hooks'
import InfiniteScrollJs from 'site/islands/InfiniteScrollJs.tsx'
import { ButtonColorsProps } from './ProductShelf.tsx'
import { ToAddToWishlist } from 'site/sdk/ga4/events/add_to_wishlist.ts'
import { toProductItem } from '$store/sdk/ga4/transform/toProductItem.ts'
import { SelectedFacet } from 'apps/vtex/utils/types.ts'
import { getAlgoliaHitInsight, type AlgoliaInsights } from 'site/utils/algoliaInsights.ts'

export interface Props {
    products: Product[] | null
    itemListName?: string
    itemListId?: string
    selectedSort: string | null
    seals: SealConfig[]
    timers: TimerConfig[]
    backgroundColors?: ButtonColorsProps
    fetch?: 'initial' | 'next' | 'previous'
    pageInfo?: PageInfo
    collectionId?: string
    sort?: string
    showInfiniteScroll: boolean
    showMosaicoMobile: boolean
    device: string
    pageType?: string
    gridCols?: string
    renderModalBuy?: boolean
    renderModalSimilars?: boolean
    isMobile?: boolean
    hasBrand?: boolean
    pixDiscount?: number
    hidePixDiscountInShelf?: boolean
    fuzzy?: 'enabled'
    facets?: SelectedFacet[]
    preload: boolean
    algoliaInsights?: AlgoliaInsights | null
}

function ProductGallery({
    products,
    itemListName,
    itemListId,
    seals,
    timers,
    backgroundColors,
    fetch = 'initial',
    pageInfo,
    collectionId,
    sort,
    selectedSort,
    showInfiniteScroll,
    showMosaicoMobile,
    device,
    pageType,
    gridCols,
    renderModalBuy,
    renderModalSimilars,
    isMobile,
    hasBrand: _hasBrand,
    pixDiscount,
    hidePixDiscountInShelf,
    fuzzy,
    facets,
    preload,
    algoliaInsights,
}: Props) {
    const id = `product-gallery-${useId()}`
    const hasBrand = _hasBrand ?? pageInfo?.pageTypes?.includes('Brand')

    return (
        <div class='flex flex-col items-center gap-6'>
            {pageInfo?.previousPage && fetch !== 'next' && showInfiniteScroll && (
                <InfiniteScroll
                    galleryId={id}
                    items={products?.length}
                    pageInfo={pageInfo}
                    sort={sort}
                    fetch='previous'
                    collectionId={collectionId}
                    seals={seals}
                    timers={timers}
                    itemListName={itemListName}
                    itemListId={itemListId}
                    selectedSort={selectedSort}
                    showInfiniteScroll={showInfiniteScroll}
                    showMosaicoMobile={showMosaicoMobile}
                    device={device}
                    renderModalBuy={renderModalBuy}
                    renderModalSimilars={renderModalSimilars}
                    hasBrand={hasBrand}
                    pixDiscount={pixDiscount}
                    hidePixDiscountInShelf={hidePixDiscountInShelf}
                    fuzzy={fuzzy}
                    facets={facets}
                />
            )}

            <div
                class='group/gallery grid grid-cols-2 gap-2 gap-y-6 items-center lg:gap-3 lg:gap-y-5 md:data-[cols-layout="cols-3"]:grid-cols-3 md:data-[cols-layout="cols-4"]:grid-cols-4 data-[cols-layout="cols-1"]:grid-cols-1 search-result-gallery'
                data-cols-layout={gridCols ?? 'cols-3'}
                data-mosaico-mobile={showMosaicoMobile && device === 'mobile'}
                id={id}
            >
                {products?.map((product, index) => {
                    const { filteredSeals } = useSeals({ seals, product })
                    const shelfSeals = filteredSeals.filter(
                        ({ showInShelf }) => showInShelf,
                    )

                    const { filteredTimers } = useTimers({ timers, product })
                    const shelfTimers = filteredTimers.filter(
                        ({ showInShelf }) => showInShelf,
                    )

                    const wishlist_analytics: ToAddToWishlist = {
                        items: [toProductItem(product, { index: 0, quantity: 1 })],
                        view: {
                            id: 'search_result_gallery',
                            name: 'Listagem de Produtos',
                            pageType: pageType ?? 'search',
                        },
                    }

                    return (
                        <div class='flex h-full group-data-[mosaico-mobile="true"]/gallery:[&:nth-child(3n)]:col-span-2'>
                            {/* <ProductInfo type='module' product={product as ProductSTD} /> */}
                            <ProductCard
                                product={mapProductToSlimProduct(product)}
                                itemListName={itemListName}
                                itemListId={itemListId}
                                seals={shelfSeals}
                                timers={shelfTimers}
                                backgroundColors={backgroundColors}
                                wishlist_analytics={wishlist_analytics}
                                renderModalBuy={renderModalBuy}
                                renderModalSimilars={renderModalSimilars &&
                                    product.additionalProperty?.some((item) =>
                                        item.propertyID === '4209'
                                    )}
                                isMobile={isMobile}
                                pixDiscount={pixDiscount}
                                hidePixDiscountInShelf={hidePixDiscountInShelf}
                                preload={preload && index === 0}
                                algoliaHit={getAlgoliaHitInsight(algoliaInsights, index)}
                            />
                        </div>
                    )
                })}
            </div>

            {pageInfo && showInfiniteScroll && (
                <InfiniteScrollJs id={id} page={pageInfo.currentPage} />
            )}
            {pageInfo?.nextPage && fetch !== 'previous' && showInfiniteScroll && (
                <InfiniteScroll
                    galleryId={id}
                    items={products?.length}
                    pageInfo={pageInfo}
                    sort={sort}
                    collectionId={collectionId}
                    seals={seals}
                    timers={timers}
                    itemListName={itemListName}
                    itemListId={itemListId}
                    selectedSort={selectedSort}
                    showInfiniteScroll={showInfiniteScroll}
                    showMosaicoMobile={showMosaicoMobile}
                    device={device}
                    renderModalBuy={renderModalBuy}
                    renderModalSimilars={renderModalSimilars}
                    hasBrand={hasBrand}
                    pixDiscount={pixDiscount}
                    hidePixDiscountInShelf={hidePixDiscountInShelf}
                    facets={facets}
                />
            )}
        </div>
    )
}

export default ProductGallery
